// frontend/src/pages/FiscalYears.jsx
import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import api from '../api/api';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';
import ExportModal from '../components/ExportModal';
import {
  Calendar,
  Plus,
  Edit,
  Trash2,
  Lock,
  Unlock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileDown,
} from 'lucide-react';

const FiscalYears = () => {
  const { activeWorkspace, refreshFiscalYears } = useWorkspace();
  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const fiscalYearColumns = [
    { key: 'label', label: 'Libellé', accessor: (y) => y?.label || '' },
    { key: 'year', label: 'Année', accessor: (y) => y?.year || '' },
    { key: 'startDate', label: 'Date début', accessor: (y) => y?.startDate ? y.startDate.split('T')[0] : '' },
    { key: 'endDate', label: 'Date fin', accessor: (y) => y?.endDate ? y.endDate.split('T')[0] : '' },
    { key: 'status', label: 'Statut', accessor: (y) => y?.status || '' },
    { key: 'isActive', label: 'Actif', accessor: (y) => y?.isActive ? 'Oui' : 'Non' },
    { key: 'closedAt', label: 'Clôturé le', accessor: (y) => y?.closedAt ? new Date(y.closedAt).toLocaleDateString('fr-FR') : '' },
  ];

  useEffect(() => {
    if (activeWorkspace) {
      loadFiscalYears();
    }
  }, [activeWorkspace]);

  const loadFiscalYears = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/fiscal-years/workspace/${activeWorkspace._id}`);
      setFiscalYears(response.data.fiscalYears || []);
    } catch (error) {
      console.error('Erreur chargement exercices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (yearId) => {
    try {
      await api.put(`/fiscal-years/${yearId}/activate`);
      await loadFiscalYears();
      refreshFiscalYears();
    } catch (error) {
      console.error('Erreur activation:', error);
      alert('Erreur lors de l\'activation');
    }
  };

  const handleClose = async (yearId) => {
    if (!await showConfirm('Voulez-vous vraiment clôturer cet exercice ?')) return;
    try {
      await api.put(`/fiscal-years/${yearId}/close`);
      await loadFiscalYears();
      refreshFiscalYears();
    } catch (error) {
      console.error('Erreur clôture:', error);
      alert('Erreur lors de la clôture');
    }
  };

  const handleUnclose = async (yearId) => {
    if (!await showConfirm('Voulez-vous vraiment déclôturer cet exercice ?')) return;
    try {
      await api.put(`/fiscal-years/${yearId}/unclose`);
      await loadFiscalYears();
      refreshFiscalYears();
    } catch (error) {
      console.error('Erreur déclôture:', error);
      alert(error.response?.data?.error || 'Erreur lors de la déclôture');
    }
  };

  const handleDelete = async (yearId) => {
    if (!await showConfirm('Voulez-vous vraiment supprimer cet exercice ?')) return;
    try {
      await api.delete(`/fiscal-years/${yearId}`);
      await loadFiscalYears();
      refreshFiscalYears();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { color: 'bg-gray-100 text-gray-700', label: 'Brouillon' },
      ACTIVE: { color: 'bg-green-100 text-green-700', label: 'Actif' },
      CLOSED: { color: 'bg-red-100 text-red-700', label: 'Clôturé' },
      ARCHIVED: { color: 'bg-gray-100 text-gray-500', label: 'Archivé' },
    };
    const badge = badges[status] || badges.DRAFT;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (!activeWorkspace) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Veuillez sélectionner un workspace</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exercices comptables</h1>
          <p className="text-sm text-gray-600 mt-1">
            Workspace : <span className="font-medium">{activeWorkspace.name}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowExportModal(true)}
            disabled={fiscalYears.length === 0}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileDown className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => {
              setEditingYear(null);
              setShowModal(true);
            }}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvel exercice
          </button>
        </div>
      </div>

      {/* Liste des exercices */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Chargement...</p>
        </div>
      ) : fiscalYears.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun exercice comptable
          </h3>
          <p className="text-gray-600 mb-4">
            Commencez par créer un exercice pour gérer votre comptabilité
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Créer le premier exercice
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {fiscalYears.map((year) => (
            <div
              key={year._id}
              className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {year.label}
                    </h3>
                    {getStatusBadge(year.status)}
                    {year.isActive && (
                      <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                        <CheckCircle className="w-3 h-3" />
                        Exercice actif
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Année</p>
                      <p className="text-sm font-medium text-gray-900">{year.year}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Période</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(year.startDate).toLocaleDateString()} →{' '}
                        {new Date(year.endDate).toLocaleDateString()}
                      </p>
                    </div>
                    {year.closedAt && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Clôturé le</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(year.closedAt).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  {year.status !== 'CLOSED' && !year.isActive && (
                    <button
                      onClick={() => handleActivate(year._id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Activer"
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                  )}

                  {year.status !== 'CLOSED' && (
                    <>
                      <button
                        onClick={() => {
                          setEditingYear(year);
                          setShowModal(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit className="w-5 h-5" />
                      </button>

                      <button
                        onClick={() => handleClose(year._id)}
                        className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Clôturer"
                      >
                        <Lock className="w-5 h-5" />
                      </button>
                    </>
                  )}

                  {year.status !== 'CLOSED' && (
                    <button
                      onClick={() => handleDelete(year._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}

                  {year.status === 'CLOSED' && (
                    <button
                      onClick={() => handleUnclose(year._id)}
                      className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      title="Déclôturer"
                    >
                      <Unlock className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Création/Édition */}
      {showModal && (
        <FiscalYearModal
          year={editingYear}
          workspaceId={activeWorkspace._id}
          onClose={() => {
            setShowModal(false);
            setEditingYear(null);
          }}
          onSuccess={() => {
            loadFiscalYears();
            refreshFiscalYears();
            setShowModal(false);
            setEditingYear(null);
          }}
        />
      )}

      {showExportModal && (
        <ExportModal
          columns={fiscalYearColumns}
          data={fiscalYears}
          title="Liste des exercices comptables"
          filename="exercices_comptables"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

// ========== MODAL CRÉATION/ÉDITION ==========
const FiscalYearModal = ({ year, workspaceId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    year: year?.year || new Date().getFullYear(),
    label: year?.label || '',
    startDate: year?.startDate ? year.startDate.split('T')[0] : '',
    endDate: year?.endDate ? year.endDate.split('T')[0] : '',
    isActive: year?.isActive || false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-remplir les dates si année change
  useEffect(() => {
    if (!year && formData.year && formData.year >= 1000 && formData.year <= 9999) {
      setFormData((prev) => ({
        ...prev,
        label: `Exercice ${formData.year}`,
        startDate: `${formData.year}-01-01`,
        endDate: `${formData.year}-12-31`,
      }));
    }
  }, [formData.year, year]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (year) {
        // Modification
        await api.put(`/fiscal-years/${year._id}`, formData);
      } else {
        // Création
        await api.post('/fiscal-years', {
          ...formData,
          workspaceId,
        });
      }
      onSuccess();
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {year ? 'Modifier l\'exercice' : 'Nouvel exercice'}
        </h2>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Année */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Année *
            </label>
            <input
              type="number"
              value={formData.year}
              onChange={(e) =>
                setFormData({ ...formData, year: parseInt(e.target.value) })
              }
              className="w-full border rounded-lg px-3 py-2"
              required
              disabled={!!year}
            />
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Libellé *
            </label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Ex: Exercice 2025"
              required
            />
          </div>

          {/* Date début */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de début *
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) =>
                setFormData({ ...formData, startDate: e.target.value })
              }
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          {/* Date fin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de fin *
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          {/* Exercice actif */}
          {!year && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                className="rounded"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">
                Définir comme exercice actif
              </label>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Enregistrement...' : year ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FiscalYears;